import React from "react"
import chunk from "lodash.chunk"
import { Box, Link } from "@chakra-ui/react"
import { Headline } from "../section/Headline"
import { GridWrapper } from "../layout/GridContainer"
import { pickRandomAlphaColor } from "../colors/colors"
import { TileExpanded } from "../card/TileExpanded"
import { TileCard, TileCardWithDescription } from "../card/Tile"
import { Container } from "../layout/Container"
import { TileModal } from "../card/TileModal"
import Dialog from "@reach/dialog"
import { Image } from "../image/Image"

function ContentItem({
  onClick,
  Expanded,
  Card,
  onReset,
  item,
  showExpanded,
  overrideGridColumn,
  showExpandedInline,
  isVisible = true,
  display,
}) {
  if (!isVisible) {
    return null
  }

  const bgColor = pickRandomAlphaColor()

  if (showExpanded && Expanded) {
    return (
      <Expanded
        showExpandedInline={showExpandedInline}
        onClick={onClick}
        onReset={onReset}
        display={display}
        item={item}
        overrideGridColumn={overrideGridColumn}
      />
    )
  }

  return (
    <Card
      onClick={Expanded && onClick}
      display={display}
      bgColor={bgColor}
      title={item.title}
      subtitle={item?.teamFields?.role}
      image={item.featuredImage}
      content={item?.content}
      overrideGridColumn={overrideGridColumn}
    />
  )
}

function ContentGroup({
  items,
  componentName,
  contentComponents,
  setHoverCoordinates,
  setInitialHoverCoordinates,
  chunkIndex,
  hoverCoordinates,
}) {
  const [expandedItem, setExpandedItem] = React.useState<number>(-1)

  return items.map((item, index) => {
    const isCurrentChunk = hoverCoordinates && hoverCoordinates.x === chunkIndex

    const showExpanded =
      isCurrentChunk && hoverCoordinates && hoverCoordinates.y === index

    const isVisible =
      typeof hoverCoordinates === `undefined` || !isCurrentChunk || showExpanded

    let gridColumn =
      componentName === `DescriptionBlockWithImage` ? `span 4` : `span 3`

    const onReset = () => setInitialHoverCoordinates()

    return (
      <React.Fragment key={item?.id}>
        {expandedItem === -1 && (
          <ContentItem
            display={{
              base: `initial`,
              sm: `none`,
              lg: `none`,
              xl: `none`,
            }}
            item={item}
            isVisible
            showExpanded={false}
            onClick={() => setExpandedItem(index)}
            overrideGridColumn={gridColumn}
            {...contentComponents}
          />
        )}
        <ContentItem
          display={{ base: `none`, sm: `initial` }}
          onClick={() => setHoverCoordinates(index)}
          onReset={onReset}
          item={item}
          isVisible={isVisible}
          showExpanded={showExpanded}
          overrideGridColumn={gridColumn}
          {...contentComponents}
        />
        {componentName === `ExpandingCard` && (
          <Dialog
            aria-label="TileModal"
            css={() => ({
              width: `100%`,
              padding: 0,
              height: `100%`,
              margin: `60px auto 0`,
            })}
            isOpen={expandedItem === index}
            onDismiss={() => setExpandedItem(-1)}
          >
            <TileModal item={item} onReset={() => setExpandedItem(-1)} />
          </Dialog>
        )}
      </React.Fragment>
    )
  })
}

const componentToReact = {
  Tile: {
    Card: TileCard,
    Expanded: TileExpanded,
  },
  ExpandingCard: {
    Card: TileCard,
    Expanded: TileExpanded,
  },
  DescriptionBlockWithImage: {
    Card: TileCardWithDescription,
  },
}

export function ContentGrid({
  title,
  graphic,
  graphicLink,
  subtitle,
  items,
  componentName = `ExpandingCard`,
  containerProps = {},
}) {
  const [hoverCoordinates, setHoverCoordinates] = React.useState(undefined)

  const contentComponents = componentToReact[componentName]
  return (
    <Container
      id={title && title.toLowerCase()?.split(" ").join("-")}
      {...containerProps}
    >
      <Box mb="50px">
        <Headline title={title} description={subtitle} />
      </Box>
      <GridWrapper>
        {chunk(items, 2, {}).map((contentItems, index) => {
          return (
            <ContentGroup
              componentName={componentName}
              key={index}
              chunkIndex={index}
              contentComponents={contentComponents}
              hoverCoordinates={hoverCoordinates}
              setInitialHoverCoordinates={() => {
                return setHoverCoordinates(undefined)
              }}
              setHoverCoordinates={y => {
                if (
                  hoverCoordinates &&
                  hoverCoordinates.x === index &&
                  hoverCoordinates &&
                  hoverCoordinates.y === y
                ) {
                  return
                }

                return setHoverCoordinates({ x: index, y })
              }}
              items={contentItems}
            />
          )
        })}
        {graphic && !graphicLink && (
          <Box
            display={{ base: `none`, lg: `block` }}
            gridColumn={{ base: "span 6" }}
            alignSelf="center"
          >
            <Box maxWidth="255px" m="0 auto">
              <Image image={graphic?.localFile?.childImageSharp} />
            </Box>
          </Box>
        )}

        {graphic && graphicLink && (
          <Box
            display={{ base: `none`, lg: `block` }}
            gridColumn={{ base: "span 6" }}
            alignSelf="center"
          >
            <Box maxWidth="255px" m="0 auto">
              <Link href={graphicLink} target="_blank">
                <Image image={graphic?.localFile} />
              </Link>
            </Box>
          </Box>
        )}
      </GridWrapper>
    </Container>
  )
}
